import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { NotificationDropdown } from '../notifications/NotificationDropdown';
import { 
  GitBranch, 
  Search, 
  Plus, 
  User, 
  LogOut,
  Shield,
  Trophy
} from 'lucide-react';

export const Header = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/?search=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <header className="header-glass sticky top-0 z-50" data-testid="main-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center gap-2 group"
            data-testid="logo-link"
          >
            <div className="w-9 h-9 bg-slate-900 rounded-lg flex items-center justify-center group-hover:bg-slate-800 transition-colors">
              <GitBranch className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold text-slate-900">Clarities</span>
          </Link>

          {/* Search Bar - Desktop */}
          <form 
            onSubmit={handleSearch}
            className="hidden md:block flex-1 max-w-md mx-8"
          >
            <div className="search-bar">
              <Search className="search-icon w-5 h-5" />
              <input
                type="text"
                placeholder="Search mindmaps..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                data-testid="search-input"
              />
            </div>
          </form>

          {/* Actions */}
          <div className="flex items-center gap-3">
            {/* Leaderboard Link */}
            <Button
              variant="ghost"
              onClick={() => navigate('/leaderboard')}
              className="text-slate-600 hover:text-orange-500 hidden sm:flex items-center gap-1"
              data-testid="leaderboard-btn"
            >
              <Trophy className="w-4 h-4" />
              <span>Leaderboard</span>
            </Button>

            {isAuthenticated ? (
              <>
                <NotificationDropdown />
                
                <Button
                  onClick={() => navigate('/new')}
                  className="btn-accent hidden sm:flex items-center gap-2"
                  data-testid="create-question-btn"
                >
                  <Plus className="w-4 h-4" />
                  <span>Ask Question</span>
                </Button>
                <Button
                  onClick={() => navigate('/new')}
                  size="icon"
                  className="btn-accent sm:hidden"
                  data-testid="create-question-btn-mobile"
                >
                  <Plus className="w-5 h-5" />
                </Button>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button 
                      className="avatar cursor-pointer hover:ring-2 hover:ring-orange-500 transition-shadow"
                      data-testid="user-menu-trigger"
                    >
                      {user?.picture ? (
                        <img src={user.picture} alt={user.name} />
                      ) : (
                        <span>{user?.name?.charAt(0).toUpperCase()}</span>
                      )}
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <div className="px-3 py-2">
                      <p className="font-medium text-sm text-slate-900">{user?.name}</p>
                      <p className="text-xs text-slate-500 truncate">{user?.email}</p>
                    </div>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem 
                      onClick={() => navigate(`/profile/${user?.user_id}`)}
                      data-testid="profile-menu-item"
                    >
                      <User className="w-4 h-4 mr-2" />
                      Profile
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => navigate('/leaderboard')}
                      data-testid="leaderboard-menu-item"
                    >
                      <Trophy className="w-4 h-4 mr-2" />
                      Leaderboard
                    </DropdownMenuItem>
                    {user?.role === 'admin' && (
                      <DropdownMenuItem 
                        onClick={() => navigate('/admin')}
                        data-testid="admin-menu-item"
                      >
                        <Shield className="w-4 h-4 mr-2" />
                        Admin Panel
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem 
                      onClick={handleLogout}
                      className="text-red-600"
                      data-testid="logout-menu-item"
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      Log out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <>
                <Button
                  variant="ghost"
                  onClick={() => navigate('/login')}
                  className="text-slate-600 hover:text-slate-900"
                  data-testid="login-btn"
                >
                  Log in
                </Button>
                <Button
                  onClick={() => navigate('/register')}
                  className="btn-primary"
                  data-testid="signup-btn"
                >
                  Sign up
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};
